import React, { useEffect } from "react";
import { Form, Input, Modal, Select } from "antd";
import { RuleObject } from "antd/es/form";
import cn from "classnames";
import api from "api";
import classes from "./CreateEmptyFileModal.module.scss";
import Spinner from "components/Spinner/Spinner";
import { getTheme } from "helpers/getTheme";
import {
  handleInputChangeWithAltFilter,
  handlePasteWithAltFilter,
} from "helpers/filterAltCodes";
import { normalizeWhitespace } from "helpers/stringCaseManager";
import fetchDocumentsData from "store/reducers/documentsSocketsCreator";
import { setUploadingDocuments } from "store/slices/dataDocumentsSlice";
import { setIsEmptyFileCreateOpen } from "store/slices/dataRoomSlice";
import { useAppDispatch, useAppSelector } from "store/hook";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const documentExtensions = ["docx", "xlsx", "pptx"] as const;

type DocumentExtension = (typeof documentExtensions)[number];

type FormValues = {
  name: string;
  extension: DocumentExtension;
};

type CheckedName = {
  name: string;
  documents: unknown[];
};

type CreateEmptyFileModalProps = {
  open: boolean;
};

const CreateEmptyFileModal = ({ open }: CreateEmptyFileModalProps) => {
  const [checking, setChecking] = React.useState(false);
  const [isFileCreating, setIsFileCreating] = React.useState(false);
  const [alreadyChecked, setAlreadyChecked] = React.useState<CheckedName[]>([]);

  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { currentFolder } = useAppSelector((store) => store.documents);
  const { messageApi } = useAppSelector((store) => store.windowState);
  const [form] = Form.useForm<FormValues>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const documentName = Form.useWatch("name", form) || "";
  const documentExtension = Form.useWatch("extension", form) || "docx";

  const onCancelClick = () => {
    dispatch(setIsEmptyFileCreateOpen(false));
  };

  const getFullFileName = (name: string, extension: DocumentExtension) => {
    const normalizedName = normalizeWhitespace(name);
    return `${normalizedName}.${extension}`;
  };

  const fullFileName = getFullFileName(documentName, documentExtension);

  const isNameValidated = fullFileName
    ? alreadyChecked.some((d) => d.name === fullFileName)
    : false;

  const isSubmitDisabled = isFileCreating || checking || !isNameValidated;

  const extraNameValidator = async (
    rules: RuleObject | null,
    value: string,
  ) => {
    const currentName = form.getFieldValue("name") || "";
    const currentExtension = form.getFieldValue("extension") || "docx";
    const currentFullFileName = getFullFileName(currentName, currentExtension);

    const isAlreadyChecked = alreadyChecked.find(
      (nameData) => nameData.name === currentFullFileName,
    );

    if (isAlreadyChecked) {
      return isAlreadyChecked.documents[0]
        ? Promise.reject(new Error(t("Documents.error.dublicate")))
        : Promise.resolve();
    }

    setChecking(true);
    try {
      const response = await api.validateDocuments({
        document_name: currentFullFileName,
        room_id: dataRoom?.id!,
      });
      setAlreadyChecked((prev) => [
        ...prev,
        { name: currentFullFileName, documents: response.data.documents },
      ]);

      if (response.data.documents[0]) {
        return Promise.reject(new Error(t("Documents.error.dublicate")));
      }

      return Promise.resolve();
    } catch (error) {
      return rules
        ? Promise.reject(
            new Error(t("Documents.modals.emptyFile.errorValidate")),
          )
        : Promise.resolve();
    } finally {
      setChecking(false);
    }
  };

  const createEmptyFile = async ({ name, extension }: FormValues) => {
    setIsFileCreating(true);
    try {
      const newDocument = await api.createEmptyDocument({
        room_id: dataRoom?.id!,
        folder_id: currentFolder[currentFolder.length - 1]?.id || null,
        document_name: fullFileName,
        extension,
      });
      dispatch(
        fetchDocumentsData({
          type: "add",
          newDocuments: [newDocument.data],
          navigate,
          roomId: dataRoom?.id,
        }),
      );
      dispatch(
        setUploadingDocuments({
          document_id: newDocument.data.id,
          document_name: newDocument.data.name,
          room_id: dataRoom?.id,
          extension: newDocument.data.extension,
          uploadProgress: 100,
        }),
      );

      onCancelClick();
      form.resetFields();
    } catch (error) {
      messageApi?.error(t("Documents.modals.emptyFile.error"));
    } finally {
      setIsFileCreating(false);
    }
  };

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      form.setFieldValue("name", filteredValue);
      form.setFields([{ name: "name", errors: [] }]);
    });
  };

  const onNamePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    handlePasteWithAltFilter(event, fullFileName, (filteredValue) => {
      form.setFieldValue("name", filteredValue);
      form.setFields([{ name: "name", errors: [] }]);
      extraNameValidator(null, filteredValue);
    });
  };

  const onExtensionChange = (value: DocumentExtension) => {
    form.setFields([{ name: "name", errors: [] }]);
    const name = form.getFieldValue("name");

    if (name) {
      form.validateFields(["name"]).catch(() => undefined);
    }
  };

  const extensionOptions = documentExtensions.map((extension) => ({
    value: extension,
    label: <span className={classes.extensionOption}>{extension}</span>,
  }));

  return (
    <Modal
      title={t("Documents.modals.emptyFile.title")}
      okText={t("Documents.modals.emptyFile.create")}
      width={450}
      open={open}
      onOk={form.submit}
      className={cn(classes.createModal, getTheme())}
      okButtonProps={{
        disabled: isSubmitDisabled,
        loading: isFileCreating || checking,
        className: classes.btn,
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      onCancel={onCancelClick}
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={{ extension: "docx" }}
        onFinish={createEmptyFile}
      >
        <Form.Item
          name='name'
          label={t("Documents.modals.emptyFile.documentName")}
          validateDebounce={500}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("Documents.modals.emptyFile.errors.emptyName"),
            },
            { validator: extraNameValidator },
          ]}
        >
          <Input
            count={{ show: true, max: 150 }}
            maxLength={150}
            prefix={checking ? <Spinner size={12} /> : <></>}
            placeholder={t("Documents.modals.emptyFile.documentPlaceholder")}
            value={documentName}
            onChange={onNameChange}
            onPaste={onNamePaste}
          />
        </Form.Item>
        <Form.Item
          name='extension'
          label={t("Documents.modals.emptyFile.extension")}
          rules={[
            {
              required: true,
              message: t("Documents.modals.emptyFile.errors.emptyExtension"),
            },
          ]}
        >
          <Select options={extensionOptions} onChange={onExtensionChange} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateEmptyFileModal;
